import {
  Building2,
  Check,
  ChevronsUpDown,
  Plus,
  Rocket,
  Sparkles,
  Zap,
} from "lucide-react";
import { Fragment, startTransition, useState } from "react";
import { Badge } from "~/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "~/components/ui/sidebar";
import { useAuth } from "~/context/AuthContext";
import { useSubscription } from "~/context/SubscriptionContext";
import { isPastDueSubscription } from "~/lib/subscription-status";
import { teamPath } from "~/lib/team-path";
import { useNavigate } from "~/lib/team-router";
import CircularLoader from "./CircularLoader";

interface TeamSwitcherProps {
  teams?: Array<Team>;
  activeTeam?: Team;
}

interface Team {
  id: string | number;
  domain: string;
  is_enterprise_plan?: boolean;
  is_pitch?: boolean;
}

function getPlanBadgeConfig(planName: string | null) {
  if (!planName) return null;

  const planLower = planName.toLowerCase();

  if (planLower === "enterprise" || planLower === "agency" || planLower === "max") {
    return {
      icon: Building2,
    };
  }
  if (planLower === "bespoke") {
    return {
      icon: Sparkles,
    };
  }
  if (
    planLower === "premium" ||
    planLower === "elite" ||
    planLower === "ultimate" ||
    planLower === "business"
  ) {
    return {
      icon: Rocket,
    };
  }
  if (planLower === "pro") {
    return {
      icon: Rocket,
    };
  }
  if (planLower === "starter") {
    return {
      icon: Zap,
    };
  }

  return {
    icon: null,
  };
}

function PaidTrialBadge() {
  return (
    <Badge
      variant="outline"
      className="text-[10px] px-1.5 py-0 h-4 w-fit font-medium border-orange-300 text-orange-700"
    >
      Trial
    </Badge>
  );
}

export default function TeamSwitcher({
  teams = [],
  activeTeam,
}: TeamSwitcherProps) {
  const { isMobile } = useSidebar();
  const { user, updateUser } = useAuth();
  const { subscription } = useSubscription();
  const currentTeamRow = teams.find(
    (t) => String(t.id) === String(activeTeam?.id)
  );
  const isEnterpriseTeam =
    Boolean(subscription?.is_enterprise_plan) ||
    Boolean(currentTeamRow?.is_enterprise_plan);
  const planBadgeName = subscription?.is_agency_plan
    ? (subscription.billing_plan?.name ?? "Agency")
    : isEnterpriseTeam
      ? "Enterprise"
      : (subscription?.billing_plan?.name ?? null);
  const missingSubscription = Boolean(subscription?.missing_subscription);
  const paidTrialActive = Boolean(subscription?.paid_trial);
  const isPastDue = isPastDueSubscription(subscription);
  const sidebarBillingStatusLabel =
    !isEnterpriseTeam &&
    (isPastDue ||
      Boolean(subscription?.trial_expired) ||
      missingSubscription)
      ? isPastDue
        ? "Past due"
        : subscription?.trial_expired
          ? "Trial expired"
          : "No active plan"
      : null;
  const isTrialPlan =
    subscription?.billing_plan?.slug === "trial" ||
    subscription?.billing_plan?.slug === "agency_trial" ||
    subscription?.billing_plan?.name?.toLowerCase() === "trial";
  const showPlanTierBadge = Boolean(
    planBadgeName &&
      !paidTrialActive &&
      !(sidebarBillingStatusLabel && (isTrialPlan || isPastDue))
  );
  const [isChangingTeam, setIsChangingTeam] = useState(false);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const navigate = useNavigate();

  async function handleTeamChange(team: Team) {
    if (isChangingTeam) return;
    setIsChangingTeam(true);
    const teamId = Number(team.id);
    startTransition(() => {
      updateUser({ team_id: teamId });
      navigate(
        teamPath({ id: teamId, domain: team.domain }, "/ai-visibility/home")
      );
    });
    setIsChangingTeam(false);
  }

  async function handleCreateTeam() {
    if (isCreatingTeam) return;
    setIsCreatingTeam(true);
    try {
      if (user?.agency) {
        navigate(
          user.agency.can_add_client === false
            ? "/agency/settings/billing"
            : "/agency/clients/new"
        );
      } else {
        navigate("/teams/create-new");
      }
    } catch (error) {
      console.error("Failed to navigate to team creation:", error);
    } finally {
      setIsCreatingTeam(false);
    }
  }

  if (!activeTeam) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="flex items-center justify-between px-2 cursor-pointer"
              tooltip="Teams"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <img
                  src={`https://img.logo.dev/${activeTeam.domain}?token=${
                    import.meta.env.VITE_LOGO_DOT_DEV_PUBLISHABLE_API_KEY
                  }`}
                  alt="Team Logo"
                  className="h-6 w-6 rounded-full shrink-0"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src =
                      "https://www.gravatar.com/avatar/b257f63497f9ca9d197dcbad473dc3f8?s=64&d=mm&r=g";
                  }}
                />
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <div className="font-semibold truncate">
                    {activeTeam.domain}
                  </div>
                  {sidebarBillingStatusLabel && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-1.5 py-0 h-4 w-fit font-medium ${
                        isPastDue
                          ? "border-red-700 text-red-700"
                          : "border-yellow-700 text-yellow-700"
                      }`}
                    >
                      {sidebarBillingStatusLabel}
                    </Badge>
                  )}
                  {activeTeam.is_pitch ? (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 h-4 w-fit font-medium border-orange-300 text-orange-700"
                    >
                      Pitch
                    </Badge>
                  ) : null}
                  {paidTrialActive && !activeTeam.is_pitch ? (
                    <PaidTrialBadge />
                  ) : null}
                  {showPlanTierBadge &&
                    !activeTeam.is_pitch &&
                    (() => {
                      const badgeConfig = getPlanBadgeConfig(planBadgeName!);
                      if (!badgeConfig) return null;
                      const Icon = badgeConfig.icon;
                      return (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 h-4 w-fit font-medium"
                        >
                          {Icon && <Icon className="size-2.5 mr-0.5" />}
                          {planBadgeName}
                        </Badge>
                      );
                    })()}
                </div>
              </div>
              <ChevronsUpDown className="shrink-0" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuGroup>
              {user?.agency ? (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Agency
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => navigate("/agency/home")}
                    className="flex items-center gap-2 cursor-pointer p-2"
                  >
                    <Building2 className="size-4 shrink-0" />
                    <span className="font-medium truncate">
                      {user.agency.name}
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : null}
              {[
                {
                  label: "Teams",
                  rows: teams.filter((team) => !team.is_pitch),
                },
              ]
                .filter((group) => group.rows.length > 0)
                .map((group, groupIndex) => (
                  <Fragment key={group.label}>
                    {groupIndex > 0 ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {group.label}
                    </DropdownMenuLabel>
                    {group.rows.map((team, index) => {
                      const isActive = team.id === activeTeam.id;
                      const rowEnterprise =
                        Boolean(team.is_enterprise_plan) ||
                        (isActive &&
                          (Boolean(subscription?.is_enterprise_plan) ||
                            Boolean(subscription?.is_agency_plan)));
                      const rowPlanName = rowEnterprise
                        ? subscription?.is_agency_plan
                          ? (subscription.billing_plan?.name ?? "Agency")
                          : "Enterprise"
                        : isActive
                          ? subscription?.billing_plan?.name
                          : null;
                      const badgeConfig =
                        isActive && rowPlanName
                          ? getPlanBadgeConfig(rowPlanName)
                          : null;
                      const Icon = badgeConfig?.icon;
                      const rowIsTrialPlan =
                        isActive &&
                        (subscription?.billing_plan?.slug === "trial" ||
                          subscription?.billing_plan?.slug === "agency_trial" ||
                          subscription?.billing_plan?.name?.toLowerCase() ===
                            "trial");
                      const rowShowBillingWarning =
                        isActive &&
                        !rowEnterprise &&
                        (isPastDue ||
                          Boolean(subscription?.trial_expired) ||
                          missingSubscription);
                      const rowShowPaidTrialBadge =
                        isActive && paidTrialActive && !team.is_pitch;
                      const showRowPlanTierBadge = Boolean(
                        badgeConfig &&
                          !team.is_pitch &&
                          !rowShowPaidTrialBadge &&
                          !(
                            rowShowBillingWarning &&
                            (rowIsTrialPlan || isPastDue)
                          )
                      );

                      return (
                        <DropdownMenuItem
                          key={index}
                          onClick={() => handleTeamChange(team)}
                          className="flex items-center justify-between cursor-pointer p-2"
                          disabled={isChangingTeam}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <img
                              src={`https://img.logo.dev/${team.domain}?token=${
                                import.meta.env
                                  .VITE_LOGO_DOT_DEV_PUBLISHABLE_API_KEY
                              }`}
                              alt="Team Logo"
                              className="h-6 w-6 rounded-full shrink-0"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src =
                                  "https://www.gravatar.com/avatar/b257f63497f9ca9d197dcbad473dc3f8?s=64&d=mm&r=g";
                              }}
                            />
                            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                              <div className="truncate">{team.domain}</div>
                              {isActive &&
                                !rowEnterprise &&
                                (isPastDue ||
                                  Boolean(subscription?.trial_expired) ||
                                  missingSubscription) && (
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] px-1.5 py-0 h-4 w-fit font-medium ${
                                      isPastDue
                                        ? "border-red-700 text-red-700"
                                        : "border-yellow-700 text-yellow-700"
                                    }`}
                                  >
                                    {isPastDue
                                      ? "Past due"
                                      : subscription?.trial_expired
                                        ? "Trial expired"
                                        : "No active plan"}
                                  </Badge>
                                )}
                              {rowShowPaidTrialBadge && <PaidTrialBadge />}
                              {showRowPlanTierBadge && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0 h-4 w-fit font-medium"
                                >
                                  {Icon && <Icon className="size-2.5 mr-0.5" />}
                                  {rowPlanName}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {isActive && (
                            <Check
                              size={16}
                              className="ml-auto mr-2 text-orange-600 shrink-0"
                            />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </Fragment>
                ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            {(user?.agency?.role === "owner" ||
              user?.agency?.role === "admin" ||
              !user?.agency) && (
              <DropdownMenuItem asChild>
                <button
                  className="flex items-center gap-2 cursor-pointer w-full h-full"
                  onClick={handleCreateTeam}
                  disabled={isCreatingTeam}
                >
                  {isCreatingTeam ? (
                    <>
                      <CircularLoader size={6} /> Creating Team...
                    </>
                  ) : (
                    <>
                      <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                        <Plus className="size-4" />
                      </div>
                      <div className="text-muted-foreground font-medium">
                        {user?.agency ? "Add client" : "Add team"}
                      </div>
                    </>
                  )}
                </button>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
